/**
 * El hilo abierto en la bandeja y en En vivo: qué ve el asesor mientras se
 * pide la conversación.
 *
 * QA de TEST, 05-oct-2026, 9:40: «Cargando conversación…» unos cinco minutos y
 * después «Reintentar». La API respondió todo en 0,2–2,5 s, sin un error; lo
 * que se midió fue el ritmo de llegada de los pedidos —huecos de 5 a 8 s y
 * ráfagas de 18 en diez segundos—, o sea el navegador reteniéndolos. Tres
 * reglas del cableado anterior convertían ese tramo de red lenta en un panel
 * trabado:
 *
 * - el panel esperaba SOLO la carga inicial, con hasta 30 s de timeout, aunque
 *   un sondeo ya hubiera traído la conversación. Ahora la muestra la primera
 *   respuesta que llega;
 * - el sondeo salía cada 2,5 s sin esperar al anterior: con la red lenta los
 *   pedidos se apilaban detrás de los que no terminaban y vencían todos.
 *   Ahora sale de a uno (`debeSondear`);
 * - cualquier sondeo fallido reemplazaba el chat abierto por el error. Ahora
 *   la conversación se sigue viendo y, con fallos seguidos, se avisa que puede
 *   no estar al día. Lo único que la suelta es lo definitivo: la conversación
 *   ya no es de quien mira (`esDefinitivo`).
 *
 * Cada pedido lleva su número y una respuesta anterior a la última aplicada no
 * pisa nada: ni la de otra conversación, ni la de un sondeo que salió antes de
 * una burbuja optimista.
 *
 * Las decisiones son puras y se prueban en Node
 * (`tests/unit/test_el_hilo_no_se_traba.py`); `useHilo.ts` las cablea.
 */

// Cada cuánto se refresca el HILO ABIERTO. Más corto que la lista a propósito:
// es una conversación de WhatsApp en curso, y el asesor tiene que ver llegar la
// respuesta del cliente sin recargar ni cambiar de conversación.
//
// 2,5 s y no 6: con 6 el aviso sonoro llegaba tarde y la conversación no se
// sentía en vivo — el asesor terminaba recargando para ver si había respuesta,
// que es exactamente lo que el polling debía evitar. Sigue siendo polling, no
// push: contra una Lambda que responde en decenas de ms, un hilo abierto por
// asesor son ~24 req/min cada uno, barato para el piloto. El reemplazo real es
// una WebSocket API en API Gateway con la Lambda de conversación publicando al
// recibir de Meta — bastante más infra: tabla de conexiones, broadcast y
// manejo de reconexión.
//
// Es un intervalo MÍNIMO, no un ritmo garantizado: con un pedido en camino el
// sondeo espera, así que con la red lenta el hilo se refresca al ritmo de la red.
export const HILO_POLL_MS = 2_500

/** Fallos seguidos con la conversación a la vista antes de avisar. Uno solo
 *  es un corte de red pasajero: avisarlo sería ruido. */
export const FALLOS_PARA_AVISAR = 2

export interface EstadoHilo<T> {
  /** La conversación abierta. */
  id: string | null
  detalle: T | null
  /** No hay nada a la vista y viene en camino. */
  cargando: boolean
  /** No hay nada a la vista y el último pedido falló. */
  error: string | null
  fallosSeguidos: number
  /** El número del último pedido aplicado: lo anterior no pisa. */
  aplicado: number
}

export type EventoHilo<T> =
  | { tipo: 'abrir'; id: string | null }
  | { tipo: 'respuesta'; id: string; pedido: number; detalle: T }
  | { tipo: 'fallo'; id: string; pedido: number; mensaje: string; definitivo: boolean }
  // Un cambio local —la burbuja optimista—. `pedido` es el último número
  // emitido: lo que ya estaba en camino se pidió antes del cambio y no lo trae.
  | { tipo: 'editar'; pedido: number; detalle?: T | null
      actualizar?: (detalle: T | null) => T | null }

/** Lo que el asesor ve. */
export interface VistaHilo<T> {
  detalle: T | null
  cargando: boolean
  error: string | null
  /** La conversación se ve, pero dejó de actualizarse. */
  sinConexion: boolean
}

export function hiloInicial<T>(): EstadoHilo<T> {
  return { id: null, detalle: null, cargando: false, error: null, fallosSeguidos: 0, aplicado: 0 }
}

export function aplicar<T>(estado: EstadoHilo<T>, evento: EventoHilo<T>): EstadoHilo<T> {
  switch (evento.tipo) {
    case 'abrir': {
      if (evento.id === estado.id) {
        // «Reintentar» sobre la misma: con la conversación a la vista no se
        // esconde; sin ella, vuelve a «Cargando…».
        if (estado.detalle) return estado
        return { ...estado, cargando: evento.id !== null, error: null }
      }
      // Otra conversación: lo de la anterior se va. La numeración sigue, así
      // que lo que la anterior tenía en camino queda atrás igual.
      return { ...hiloInicial<T>(), id: evento.id, cargando: evento.id !== null,
               aplicado: estado.aplicado }
    }
    case 'respuesta':
      if (evento.id !== estado.id || evento.pedido <= estado.aplicado) return estado
      return { ...estado, detalle: evento.detalle, cargando: false, error: null,
               fallosSeguidos: 0, aplicado: evento.pedido }
    case 'fallo':
      if (evento.id !== estado.id || evento.pedido <= estado.aplicado) return estado
      if (evento.definitivo) {
        return { ...estado, detalle: null, cargando: false, error: evento.mensaje,
                 fallosSeguidos: 0, aplicado: evento.pedido }
      }
      if (estado.detalle) return { ...estado, fallosSeguidos: estado.fallosSeguidos + 1 }
      return { ...estado, cargando: false, error: evento.mensaje,
               fallosSeguidos: estado.fallosSeguidos + 1 }
    case 'editar': {
      const detalle = evento.actualizar
        ? evento.actualizar(estado.detalle)
        : (evento.detalle ?? null)
      return { ...estado, detalle, aplicado: Math.max(estado.aplicado, evento.pedido) }
    }
  }
}

export function vista<T>(estado: EstadoHilo<T>): VistaHilo<T> {
  return {
    detalle: estado.detalle,
    cargando: estado.cargando,
    error: estado.error,
    sinConexion: estado.detalle !== null && estado.fallosSeguidos >= FALLOS_PARA_AVISAR,
  }
}

/** Lo que el asesor ve después de cada evento, desde el panel vacío. */
export function recorrer<T>(eventos: EventoHilo<T>[]): VistaHilo<T>[] {
  let estado = hiloInicial<T>()
  return eventos.map((evento) => {
    estado = aplicar(estado, evento)
    return vista(estado)
  })
}

/** ¿Sale el sondeo? Con un pedido en camino espera su turno: es lo que evita
 *  que la red lenta apile pedidos. Mientras el asesor envía tampoco sale, para
 *  no pisar la burbuja optimista. */
export function debeSondear(enVuelo: number, pausado: boolean): boolean {
  return enVuelo === 0 && !pausado
}

/** ¿El fallo suelta la conversación? Solo si ya no es de quien mira (403
 *  `no_asignada`) o no existe. Un timeout, un 5xx o un 429 son la red o el
 *  servidor: la conversación se sigue viendo. */
export function esDefinitivo(status: number, codigo: string): boolean {
  return codigo === 'no_asignada' || status === 403 || status === 404
}

/** Lo mínimo de un mensaje que hace falta para unir dos listas. */
interface MensajeDelHilo {
  message_id?: string
  timestamp: string
  sender?: string
  text?: string
}

/** El historial pedido una vez, más la sesión que llega en cada sondeo
 *  (`?vista=sesion`). Por id: el mismo mensaje viene en las dos listas en el
 *  borde, y el del sondeo gana porque trae el estado de entrega al día. En
 *  orden de hora. */
export function unirMensajes<M extends MensajeDelHilo>(historial: M[], sesion: M[]): M[] {
  const clave = (m: M) => m.message_id || `${m.timestamp}|${m.sender ?? ''}|${m.text ?? ''}`
  const porClave = new Map<string, M>()
  for (const m of historial) porClave.set(clave(m), m)
  for (const m of sesion) porClave.set(clave(m), m)
  return [...porClave.values()].sort((a, b) =>
    a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0)
}
